import React, {
  ChangeEvent,
  useState,
} from 'react';
import classnames from 'classnames';
import { FieldProps } from 'formik';
import { CaretIcon } from '../../../icons';

import styles from './SelectField.module.scss';

export type SelectFieldProps<T> = FieldProps<T> & BasicFieldProps & {
  data: SelectOption[]
  decode: (v: string) => string
  format: (v: string) => string
};

function SelectField<T extends string>({
  field: {
    name,
    value,
    onChange,
  },
  form,
  label,
  disabled,
  required,
  data,
  decode = (v) => v,
  format = (v) => v,
  className,
  helper,
}: SelectFieldProps<T>): JSX.Element {
  const [fieldFocus, setFieldFocus] = useState(false);
  function handleFocus() {
    setFieldFocus(true);
  }

  function handleBlur() {
    setFieldFocus(false);
  }

  function handleChange(e: ChangeEvent<HTMLSelectElement>) {
    onChange({
      target: {
        name,
        value: format(e?.target?.value),
      },
    });
    setFieldFocus(false);
  }

  const valid = (form?.touched?.[name] || false) && !(form?.errors?.[name] || false);
  const invalid = (form?.touched?.[name] || false) && (form?.errors?.[name] || false);
  const filled = (value !== (undefined && null) && value.length > 0);

  return (
    <div className={classnames(
      className,
      styles.selectFieldContainer,
    )}
    >
      <div
        data-testid={`select-field-${name}`}
        className={classnames(styles.selectField, {
          [styles.selectFieldFocus]: fieldFocus,
          [styles.selectFieldValid]: valid,
          [styles.selectFieldInvalid]: invalid,
          [styles.selectFieldFilled]: filled,
          [styles.selectFieldDisabled]: disabled,
        })}
      >
        <select
          id={name}
          name={name}
          onChange={handleChange}
          value={decode(value)}
          disabled={disabled}
          onFocus={handleFocus}
          onBlur={handleBlur}
        >
          {(!required || value === '') && <option value="" aria-labelledby={name} />}
          {data.map((option) => (
            <option
              key={option.value}
              value={option.value}
            >
              {option.label}
            </option>
          ))}
        </select>
        {label && (
          <label htmlFor={name}>
            {label}
          </label>
        )}
        <CaretIcon className={styles.caretIcon} />
      </div>
      {helper && !invalid && (
        <span
          data-testid={`helper-select-field-${name}`}
          className={styles.helper}
        >
          {helper}
        </span>
      )}
    </div>
  );
}

export default SelectField;
